// src/components/common/ImageUploadDialog.js
import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { styled, useTheme } from "@mui/material/styles";
import axiosInstance from "../../api/axiosConfig";
import Lottie from "lottie-react";
import animationData from "../../assets/icons/infinate_loader.json";

const UploadDialogContent = styled(DialogContent)(({ theme }) => ({
  paddingTop: theme.spacing(1) + ' !important',
}));

const UploadArea = styled(Box)(({ theme, isDraggingOver }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    border: `2px dashed ${isDraggingOver ? theme.palette.primary.main : theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: isDraggingOver ? theme.palette.action.hover : theme.palette.background.paper,
    cursor: 'pointer',
    transition: 'background-color 0.2s ease-in-out, border-color 0.2s ease-in-out',
    '&:hover': {
        backgroundColor: theme.palette.action.hover,
    }
}));

const PreviewImage = styled("img")(({ theme }) => ({
  maxWidth: "100%",
  maxHeight: 200,
  objectFit: "contain",
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(2),
  boxShadow: theme.shadows[2],
  border: `1px solid ${theme.palette.divider}`,
}));


const ImageUploadDialog = ({
  open,
  onClose,
  onUploadComplete,
  showSnackbar,
  additionalFormData = {},
  descriptionLabel = "Image Label / Description",
  dialogTitle = "Upload New Image",
  // Removed imageFieldName prop as helper uses 'image'
}) => {
  const theme = useTheme();
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [filePreview, setFilePreview] = useState(null);
  const [description, setDescription] = useState("");
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  const [dialogError, setDialogError] = useState('');
  const fileInputRef = useRef(null);
  const resolvedUseSchoolAssetApi =
    Boolean(additionalFormData?.useSchoolAssetApi) ||
    ['SIGNATURES', 'SEALS', 'LOGOS'].includes(String(additionalFormData?.assetType || '').toUpperCase()) ||
    /signature|seal|logo/i.test(String(additionalFormData?.category || ''));
  const requireDescription = !resolvedUseSchoolAssetApi;

  useEffect(() => {
    if (!open) {
      setFile(null);
      setFilePreview(null);
      setDescription("");
      setIsUploading(false);
      setIsDraggingOver(false);
      setDialogError('');
    }
  }, [open]);

  const handleFileChange = (selectedFile) => {
    setDialogError('');
    if (selectedFile) {
       const maxSizeMB = 1; 
       if (!selectedFile.type.startsWith("image/")) {
           setDialogError(`Please select a valid image file (JPG, PNG, GIF, etc.).`);
           return;
       }
       if (selectedFile.size > maxSizeMB * 1024 * 1024) {
           setDialogError(`File size exceeds ${maxSizeMB}MB limit.`);
           return;
       }

      setFile(selectedFile);
      const reader = new FileReader();
      reader.onloadend = () => {
        setFilePreview(reader.result);
      };
      reader.readAsDataURL(selectedFile);
      if (resolvedUseSchoolAssetApi) {
        setTimeout(() => handleUpload(selectedFile), 0);
      }
    }
  };

  const handleManualFileSelect = (e) => {
    handleFileChange(e.target.files?.[0]);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDraggingOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDraggingOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      handleFileChange(e.dataTransfer.files[0]);
      e.dataTransfer.clearData();
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  // --- Corrected handleUpload ---
  const handleUpload = async (fileOverride = null) => {
    setDialogError('');

    const imageType = additionalFormData?.assetType;
    const schoolId = additionalFormData?.schoolId; // Extract schoolId too
    const category = additionalFormData?.category;
    const activeFile = fileOverride || file;
    // Validation
    if (!activeFile) {
        setDialogError("Please select an image file.");
        return;
    }
    if (!imageType) {
        console.error("Error: assetType missing in additionalFormData for ImageUploadDialog.");
        setDialogError("Configuration error: Image type is missing.");
        return;
    }
    // schoolId might be optional for some uploads, helper handles fallback
    if (requireDescription && !description) {
        setDialogError(`Please provide a ${descriptionLabel}.`);
        return;
    }

    setIsUploading(true);
    try {
      let uploadedUrl = null;
      let uploadResponse = null;

      if (resolvedUseSchoolAssetApi) {
        if (!schoolId) {
          setDialogError("School ID is required.");
          setIsUploading(false);
          return;
        }
        if (!category) {
          setDialogError("Category is required.");
          setIsUploading(false);
          return;
        }
        const formData = new FormData();
        formData.append('image', activeFile);
        formData.append('schoolId', String(schoolId));
        formData.append('category', String(category));
        if (imageType) {
          formData.append('type', imageType);
        }
        const resp = await axiosInstance.post('/api/images/upload/school-asset', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploadResponse = resp?.data;
        if (uploadResponse && typeof uploadResponse === 'object') {
          const resolvedField = additionalFormData?.targetField;
          if (resolvedField && uploadResponse[resolvedField]) {
            uploadedUrl = uploadResponse[resolvedField];
          }
        }
      } else {
        // Pass file, imageType, schoolId (if available), and description
        uploadedUrl = await axiosInstance.uploadImage(
            activeFile,
            imageType,
            schoolId,
            description,
            additionalFormData?.categoryName
        );
      }

      showSnackbar("Image uploaded successfully!", "success");

      if (onUploadComplete) {
        onUploadComplete(uploadedUrl || uploadResponse);
      }
      onClose();

    } catch (error) {
      console.error("Upload error:", error);
      const errorMessage = error?.message || "An unknown error occurred during upload.";
      setDialogError(`Upload failed: ${errorMessage}`);
    } finally {
      setIsUploading(false);
    }
  };
  // --- End Corrected handleUpload ---

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ style: { borderRadius: theme.shape.borderRadius * 1.5 } }}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle
        sx={{
          fontWeight: "bold",
          fontSize: "1.3rem",
          textAlign: "center",
          m: 0,
          py: 1.5,
          px: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {dialogTitle}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
          disabled={isUploading}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <UploadDialogContent>
        {isUploading ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
            <Lottie animationData={animationData} style={{ height: 120, width: 120 }} loop={true} />
            <Typography sx={{ mt: 2 }}>Uploading, please wait...</Typography>
          </Box>
        ) : (
          <Stack alignItems="center" spacing={2} width="100%">
            {dialogError && <Alert severity="error" sx={{ width: '100%', mb: 1 }}>{dialogError}</Alert>}
            {filePreview && <PreviewImage src={filePreview} alt="Preview" />}
            <UploadArea onClick={triggerFileInput} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} isDraggingOver={isDraggingOver}>
              <AddPhotoAlternateIcon sx={{ fontSize: 40, color: theme.palette.primary.light, mb: 1 }} />
              <Typography variant="body1" color="textSecondary" fontWeight="medium">
                {isDraggingOver ? "Drop image here" : "Drag & Drop or Click"}
              </Typography>
              <Typography variant="caption" color="textSecondary">to upload photo (Max 2MB)</Typography>
            </UploadArea>
            <input ref={fileInputRef} id={`image-upload-input-dialog-${Math.random().toString(36).substring(7)}`} hidden accept="image/*" type="file" onChange={handleManualFileSelect} />

            {requireDescription && (
              <TextField
                fullWidth
                required
                variant="outlined"
                label={`${descriptionLabel}*`}
                placeholder="e.g., Principal's Signature 2024"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
              />
            )}
          </Stack>
        )}
      </UploadDialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "space-between", borderTop: `1px solid ${theme.palette.divider}` }}>
        <Button onClick={onClose} disabled={isUploading} color="inherit" size="small"> Cancel </Button>
        {!resolvedUseSchoolAssetApi && (
          <Button
            variant="contained"
            disabled={!file || (requireDescription && !description) || isUploading}
            onClick={handleUpload}
            size="small"
            startIcon={ isUploading ? <CircularProgress size={18} color="inherit" /> : null }
          >
            {isUploading ? "Uploading..." : "Upload"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

ImageUploadDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onUploadComplete: PropTypes.func,
  showSnackbar: PropTypes.func.isRequired,
  additionalFormData: PropTypes.shape({
      assetType: PropTypes.string,
      schoolId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]), // schoolId might be passed
      useSchoolAssetApi: PropTypes.bool,
      category: PropTypes.string,
      targetField: PropTypes.string,
  }),
  descriptionLabel: PropTypes.string,
  dialogTitle: PropTypes.string,
  // Removed imageFieldName prop type
};

export default ImageUploadDialog;
